import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Meeting, MeetingAttendee, UpdateSummaryRequest, AgendaItem, PrepComment } from '../../../types/meeting';
import { LiveState, LiveParticipant } from '../../../types/live';
import meetingsAPI from '../../../services/meetingsApi';
import liveAPI from '../../../services/liveApi';
import { useAuth } from '../../../contexts/AuthContext';
import MaterialPickerDialog from './MaterialPickerDialog';
import MeetingStatusBadge from './MeetingStatusBadge';
import MeetingOutcomesActions from './panels/MeetingOutcomesActions';
import MeetingAgendaPanel from './panels/MeetingAgendaPanel';
import MeetingAttendeesPanel from './panels/MeetingAttendeesPanel';
import MeetingMaterialsPanel, { MeetingFile } from './panels/MeetingMaterialsPanel';
import MeetingBriefingPanel from './panels/MeetingBriefingPanel';
import MeetingTranscriptPanel from './panels/MeetingTranscriptPanel';
import MeetingMinutesPanel from './panels/MeetingMinutesPanel';
import MeetingChatPanel, { ChatMessage, extractHistory } from './panels/MeetingChatPanel';
import MaterialReaderPanel from './panels/MaterialReaderPanel';
import MeetingPrepActionsPanel from './panels/MeetingPrepActionsPanel';
import '../../../styles/meetings.css';

type WorkspaceTab = 'prepare' | 'meeting' | 'outcomes';
type GeneratingKind = 'agenda' | 'transcript' | 'summary' | null;

interface MeetingWorkspaceViewProps {
  meeting: Meeting;
  onBack: () => void;
  onMeetingUpdated: (meeting: Meeting) => void;
  onError: (message: string) => void;
}

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return 'Not scheduled';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const MeetingWorkspaceView: React.FC<MeetingWorkspaceViewProps> = ({
  meeting,
  onBack,
  onMeetingUpdated,
  onError
}) => {
  const navigate = useNavigate();
  const { isSecretary, user } = useAuth();
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('prepare');
  const [generating, setGenerating] = useState<GeneratingKind>(null);
  const [files, setFiles] = useState<MeetingFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [liveParticipants, setLiveParticipants] = useState<LiveParticipant[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [startingLive, setStartingLive] = useState(false);
  const [outcomesApproved, setOutcomesApproved] = useState<boolean>(!!meeting.summary?.approved);
  // The material open in the reader, and the reader's own prep-note counts per
  // material. Counts are the caller's own notes only — the API never returns
  // anyone else's.
  const [readingFile, setReadingFile] = useState<MeetingFile | null>(null);
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
  // Advisor conversation is lifted here (rather than inside MeetingChatPanel) so
  // it survives tab switches — which unmount the panel — and is shared by the
  // Prepare and Outcomes instances. Seeded once from the meeting's stored history.
  const [advisorMessages, setAdvisorMessages] = useState<ChatMessage[]>(() => extractHistory(meeting));

  // Keep the approval badge in sync with the backend summary; editing the
  // summary resets approval (handled on save below).
  useEffect(() => {
    setOutcomesApproved(!!meeting.summary?.approved);
  }, [meeting.summary?.approved]);

  // The Meeting Transcript tab is secretary-only; if a non-secretary is somehow
  // on it (e.g. role change), fall back to Prepare so the raw transcript is
  // never rendered to them.
  useEffect(() => {
    if (!isSecretary && activeTab === 'meeting') setActiveTab('prepare');
  }, [isSecretary, activeTab]);

  // Full-replace save of the edited outcomes. A successful save resets approval
  // on the backend, so the badge clears and export is blocked until re-approval.
  // The loaded meeting's _etag goes out as If-Match, so a save that would
  // overwrite someone else's is rejected and raised to the editor as a conflict
  // rather than applied.
  const handleSaveSummary = async (draft: UpdateSummaryRequest) => {
    const updated = await meetingsAPI.updateSummary(meeting.meeting_id, draft, meeting._etag);
    onMeetingUpdated(updated);
    setOutcomesApproved(false);
  };

  // Pulls the server's current version into the workspace after a conflict. The
  // editor keeps its draft until the user explicitly discards it.
  const handleReloadMeeting = async () => {
    const latest = await meetingsAPI.getMeeting(meeting.meeting_id);
    onMeetingUpdated(latest);
    return latest;
  };

  const handleSaveAgenda = async (items: AgendaItem[]) => {
    const updated = await meetingsAPI.updateAgenda(meeting.meeting_id, items);
    onMeetingUpdated(updated);
  };

  const loadFiles = useCallback(async () => {
    try {
      const list = await meetingsAPI.listFiles(meeting.meeting_id);
      setFiles(list);
    } catch {
      // Non-fatal; materials list just stays empty
    }
  }, [meeting.meeting_id]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // Seed the per-material note counts in one list call, so the materials panel
  // shows where the reader has already prepared without opening each document.
  // Non-fatal: with no counts the rows simply show none.
  useEffect(() => {
    let cancelled = false;
    meetingsAPI
      .listPrepComments(meeting.meeting_id)
      .then((comments) => {
        if (cancelled) return;
        const counts: Record<string, number> = {};
        for (const c of comments) counts[c.file_id] = (counts[c.file_id] || 0) + 1;
        setCommentCounts(counts);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [meeting.meeting_id]);

  // The reader owns the notes for the material it has open, so it reports the
  // authoritative list back rather than the count being incremented by guess.
  const handleCommentsChanged = useCallback((fileId: string, comments: PrepComment[]) => {
    setCommentCounts((prev) => ({ ...prev, [fileId]: comments.length }));
  }, []);

  // Track live session status so members see "Join Live" whenever a session is
  // actually live (independent of prepared status). Reuses the live SSE stream;
  // degrades to not-live if the stream endpoint is unavailable.
  useEffect(() => {
    const applyState = (state: LiveState) => {
      setIsLive(state.is_live);
      setLiveParticipants(state.is_live ? state.participants ?? [] : []);
    };
    const handle = liveAPI.openStream(meeting.meeting_id, {
      onLiveState: applyState,
      onSlideChanged: (_ref, state) => {
        if (state) applyState(state);
      },
    });
    return () => handle.close();
  }, [meeting.meeting_id]);

  const goToLive = () => navigate(`/meetings/${meeting.meeting_id}/live`);

  // Secretary go-live: start the session with the picked deck, then enter the
  // live view. A 409 (meeting not prepared) surfaces the server's message.
  const handleStartLive = async (materialFileIds: string[]) => {
    setStartingLive(true);
    try {
      await liveAPI.startLive(meeting.meeting_id, materialFileIds);
      setPickerOpen(false);
      goToLive();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to start live session');
    } finally {
      setStartingLive(false);
    }
  };

  const runGeneration = async (kind: Exclude<GeneratingKind, null>, action: () => Promise<Meeting>) => {
    setGenerating(kind);
    try {
      const updated = await action();
      onMeetingUpdated(updated);
    } catch (err) {
      onError(err instanceof Error ? err.message : `Failed to generate ${kind}`);
    } finally {
      setGenerating(null);
    }
  };

  const handleAddAttendee = async (input: { name: string; role?: string; userId?: string }) => {
    try {
      const newAttendee: MeetingAttendee = {
        attendee_id: crypto.randomUUID(),
        name: input.name,
        role: input.role || null,
        organization: null,
        stance: null,
        is_generated: false,
        user_id: input.userId || null
      };
      const updated = await meetingsAPI.updateMeeting(meeting.meeting_id, {
        attendees: [...(meeting.attendees || []), newAttendee]
      });
      onMeetingUpdated(updated);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to add attendee');
      throw err;
    }
  };

  const handleRemoveAttendee = async (attendee: MeetingAttendee) => {
    try {
      const remaining = meeting.attendees.filter((a) => a.attendee_id !== attendee.attendee_id);
      const updated = await meetingsAPI.updateMeeting(meeting.meeting_id, { attendees: remaining });
      onMeetingUpdated(updated);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to remove attendee');
    }
  };

  const handleUpload = async (selected: File[]) => {
    setIsUploading(true);
    // Upload sequentially but isolate failures per file so one bad upload does
    // not abort the rest of the batch; report a concise summary at the end.
    const failed: string[] = [];
    try {
      for (const file of selected) {
        try {
          await meetingsAPI.uploadFile(meeting.meeting_id, file);
        } catch {
          failed.push(file.name);
        }
      }
      await loadFiles();
    } finally {
      setIsUploading(false);
    }
    if (failed.length > 0) {
      const uploaded = selected.length - failed.length;
      onError(`${uploaded} of ${selected.length} uploaded. Failed: ${failed.join(', ')}`);
    }
  };

  const handleDownloadFile = async (file: MeetingFile) => {
    // Fetch the blob through the auth interceptor, then save via an object URL.
    // A bare anchor href to the download endpoint carries no Bearer token and
    // returns the SPA index.html (401), which surfaced as a download error.
    try {
      const blob = await meetingsAPI.downloadFile(file.file_id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to download file');
    }
  };

  const handleDeleteFile = async (file: MeetingFile) => {
    try {
      await meetingsAPI.deleteFile(file.file_id);
      setFiles((prev) => prev.filter((f) => f.file_id !== file.file_id));
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to delete file');
    }
  };

  return (
    <div className="meeting-workspace">
      <div className="meeting-workspace-header">
        <button className="meeting-back-btn" onClick={onBack}>← All meetings</button>
        <div className="meeting-workspace-title-row">
          <h1 className="meeting-workspace-title">
            {meeting.title}
            <MeetingStatusBadge meeting={meeting} isLive={isLive} />
          </h1>
          {isLive ? (
            <button className="meeting-btn meeting-btn-primary live-entry-btn" onClick={goToLive}>
              ● Join Live
            </button>
          ) : (
            isSecretary &&
            (meeting.status === 'prepared' ? (
              <button
                className="meeting-btn meeting-btn-primary live-entry-btn"
                onClick={() => setPickerOpen(true)}
              >
                ● Go Live
              </button>
            ) : (
              <span className="live-entry-wrap" title="Meeting must be in Prepared state to go live">
                <button className="meeting-btn meeting-btn-primary live-entry-btn" disabled>
                  ● Go Live
                </button>
              </span>
            ))
          )}
        </div>
        <div className="meeting-workspace-subtitle">
          <span>📅 {formatDateTime(meeting.scheduled_at)}</span>
          <span>⏱ {meeting.duration_minutes} min</span>
          {meeting.committee && <span>👥 {meeting.committee}</span>}
          {meeting.location && <span>📍 {meeting.location}</span>}
          <span>{files.length} materials</span>
          {isLive && liveParticipants.length > 0 && (
            <span
              className="meeting-workspace-joined"
              title={liveParticipants.map((p) => p.display_name).join(', ')}
            >
              👥 {liveParticipants.length} joined: {liveParticipants.map((p) => p.display_name).join(', ')}
            </span>
          )}
        </div>
        <div className="meeting-tabs">
          <button className={`meeting-tab ${activeTab === 'prepare' ? 'active' : ''}`} onClick={() => setActiveTab('prepare')}>
            Prepare
          </button>
          {/* The raw meeting transcript is a secretary-only artefact. */}
          {isSecretary && (
            <button className={`meeting-tab ${activeTab === 'meeting' ? 'active' : ''}`} onClick={() => setActiveTab('meeting')}>
              Meeting Transcript
            </button>
          )}
          <button className={`meeting-tab ${activeTab === 'outcomes' ? 'active' : ''}`} onClick={() => setActiveTab('outcomes')}>
            Outcomes
          </button>
        </div>
      </div>

      <div className="meeting-workspace-body">
        {activeTab === 'prepare' && (
          <div className="meeting-columns meeting-columns-fill">
            <div className="meeting-col-main">
              <MeetingBriefingPanel
                meeting={meeting}
                onError={onError}
                collapsible
              />
              <MeetingChatPanel
                meeting={meeting}
                compact
                collapsible
                messages={advisorMessages}
                onMessagesChange={setAdvisorMessages}
              />
            </div>
            <div className="meeting-col-side">
              {/* First on the prep rail: what you personally have to do is the
                  reason to open this tab. */}
              <MeetingPrepActionsPanel
                meetingId={meeting.meeting_id}
                attendees={meeting.attendees || []}
                canManage={isSecretary}
                currentUserId={user?.id}
              />
              <MeetingAttendeesPanel
                meeting={meeting}
                canManage={isSecretary}
                onAddAttendee={handleAddAttendee}
                onRemoveAttendee={handleRemoveAttendee}
              />
              <MeetingAgendaPanel
                meeting={meeting}
                isGenerating={generating === 'agenda'}
                canEdit={isSecretary}
                onGenerate={(focus) => runGeneration('agenda', () => meetingsAPI.generateAgenda(meeting.meeting_id, focus))}
                onSave={handleSaveAgenda}
                collapsible
              />
              <MeetingMaterialsPanel
                files={files}
                isUploading={isUploading}
                canManage={isSecretary}
                onUpload={handleUpload}
                onDownload={handleDownloadFile}
                onDelete={handleDeleteFile}
                onOpen={setReadingFile}
                commentCounts={commentCounts}
              />
            </div>
          </div>
        )}

        {activeTab === 'meeting' && isSecretary && (
          <MeetingTranscriptPanel
            meeting={meeting}
            isGenerating={generating === 'transcript'}
            onUploadTranscript={(text) => runGeneration('transcript', () => meetingsAPI.uploadTranscript(meeting.meeting_id, text))}
          />
        )}

        {activeTab === 'outcomes' && (
          <div className="meeting-columns meeting-columns-fill meeting-columns-narrow-main">
            <div className="meeting-col-main">
              <MeetingChatPanel
                meeting={meeting}
                compact
                collapsible
                messages={advisorMessages}
                onMessagesChange={setAdvisorMessages}
              />
            </div>
            <div className="meeting-col-side">
              <MeetingMinutesPanel
                meeting={meeting}
                isGenerating={generating === 'summary'}
                canEdit={isSecretary}
                approved={outcomesApproved}
                onGenerate={() => runGeneration('summary', () => meetingsAPI.generateSummary(meeting.meeting_id))}
                onSave={handleSaveSummary}
                onReload={handleReloadMeeting}
              />
              <MeetingOutcomesActions
                meeting={meeting}
                canApprove={isSecretary}
                approved={outcomesApproved}
                onApproved={(updated) => { onMeetingUpdated(updated); setOutcomesApproved(true); }}
                onError={onError}
                onReload={handleReloadMeeting}
              />
            </div>
          </div>
        )}
      </div>

      <MaterialPickerDialog
        isOpen={pickerOpen}
        files={files}
        busy={startingLive}
        onCancel={() => setPickerOpen(false)}
        onStart={handleStartLive}
      />

      <MaterialReaderPanel
        meetingId={meeting.meeting_id}
        file={readingFile}
        onClose={() => setReadingFile(null)}
        onDownload={handleDownloadFile}
        onCommentsChanged={handleCommentsChanged}
      />
    </div>
  );
};

export default MeetingWorkspaceView;
